/**
 * A2 — Confirmação dos campos extraídos.
 *
 * Recebe `extracted` da `<ReceiptCapture>` e mostra 4 campos editáveis:
 *   - Valor (R$, máscara monetária)
 *   - Data (date picker nativo)
 *   - Estabelecimento (texto)
 *   - Categoria (texto com sugestões via datalist)
 *
 * Alerta amarelo no topo se `confidence < 0.7` ("Confira os dados antes
 * de registrar.") — não bloqueia o botão.
 *
 * Ao clicar em "Registrar", invoca o callback `onSubmit({amount,
 * category, description, date, type: "expense"})` — caller chama o
 * endpoint existente `POST /api/finance/transactions` (a confirmação
 * humanizada vem do `register_expense` Fase 1 de graça).
 */
import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, X } from "lucide-react";


const CONFIDENCE_THRESHOLD_WARN = 0.7;

const DEFAULT_CATEGORIES = [
  "Alimentação", "Mercado", "Restaurante", "Transporte",
  "Saúde", "Lazer", "Casa", "Vestuário", "Serviços", "Outros",
];

// Mapeia category_suggested da A1 (snake/lower, sem acento) pra label.
const A1_TO_LABEL = {
  mercado: "Mercado",
  restaurante: "Restaurante",
  transporte: "Transporte",
  saude: "Saúde",
  lazer: "Lazer",
  casa: "Casa",
  vestuario: "Vestuário",
  servicos: "Serviços",
  outros: "Outros",
};


function _formatBrlInput(cents) {
  // cents (int) → "R$ 87,50" (display string)
  if (cents == null || Number.isNaN(cents)) return "";
  const v = (cents / 100).toFixed(2).replace(".", ",");
  return v;
}


function _parseBrlInput(text) {
  // "87,50" / "R$ 87,50" / "87.50" → cents int
  if (!text) return 0;
  const cleaned = String(text)
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")  // pt-BR: ponto = milhar; remove
    .replace(",", ".");
  const n = parseFloat(cleaned);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}


export default function ReceiptConfirm({
  extracted,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) {
  const [amountText, setAmountText] = useState(
    _formatBrlInput(extracted?.amount_cents),
  );
  const [dateText, setDateText] = useState(extracted?.date || "");
  const [merchant, setMerchant] = useState(extracted?.merchant || "");
  const [category, setCategory] = useState(
    A1_TO_LABEL[extracted?.category_suggested] || extracted?.category_suggested || "",
  );

  // Refresh quando extracted muda (segundo extract de outro upload).
  useEffect(() => {
    setAmountText(_formatBrlInput(extracted?.amount_cents));
    setDateText(extracted?.date || "");
    setMerchant(extracted?.merchant || "");
    setCategory(
      A1_TO_LABEL[extracted?.category_suggested] || extracted?.category_suggested || "",
    );
  }, [extracted]);

  const lowConfidence = useMemo(
    () => (extracted?.confidence ?? 0) < CONFIDENCE_THRESHOLD_WARN,
    [extracted],
  );

  const amountCents = _parseBrlInput(amountText);
  const canSubmit = !isSubmitting && amountCents > 0 && Boolean(dateText);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    // Shape compatível com o endpoint existente (modelo
    // CreateTransactionRequest em backend/models/finance.py): amount
    // float, description, category, date, type.
    onSubmit?.({
      type: "expense",
      amount: amountCents / 100,
      description: (merchant || extracted?.notes || "Despesa por foto").trim(),
      category: (category || "Outros").trim(),
      date: dateText || undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="receipt-confirm"
      className="bg-[#0d1220] border border-white/[0.08] rounded-2xl p-6 safe-modal-pb w-full max-w-md"
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl font-bold text-white">
          Confirmar despesa
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 -mr-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Cancelar"
          data-testid="receipt-confirm-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {lowConfidence && (
        <div
          data-testid="receipt-confirm-low-confidence-warning"
          className="mb-4 flex gap-2 items-start px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-sm"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Confira os dados antes de registrar.</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="receipt-amount"
            className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider"
          >
            Valor
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 font-medium">
              R$
            </span>
            <input
              id="receipt-amount"
              type="text"
              inputMode="decimal"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
              placeholder="0,00"
              data-testid="receipt-confirm-amount"
              className="w-full pl-12 pr-4 py-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white text-xl font-bold placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors"
              autoFocus
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="receipt-date"
            className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider"
          >
            Data
          </label>
          <input
            id="receipt-date"
            type="date"
            value={dateText}
            onChange={(e) => setDateText(e.target.value)}
            data-testid="receipt-confirm-date"
            className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-cyan-500/30 transition-colors"
          />
        </div>

        <div>
          <label
            htmlFor="receipt-merchant"
            className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider"
          >
            Estabelecimento
          </label>
          <input
            id="receipt-merchant"
            type="text"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            placeholder="Ex: Pão de Açúcar"
            data-testid="receipt-confirm-merchant"
            className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors"
          />
        </div>

        <div>
          <label
            htmlFor="receipt-category"
            className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider"
          >
            Categoria
          </label>
          <input
            id="receipt-category"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            list="receipt-category-options"
            placeholder="Ex: Mercado"
            data-testid="receipt-confirm-category"
            className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors"
          />
          <datalist id="receipt-category-options">
            {DEFAULT_CATEGORIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
      </div>

      <div className="flex gap-3 pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-3 rounded-xl border border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
          data-testid="receipt-confirm-cancel"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="receipt-confirm-submit"
          className="flex-1 px-4 py-3 rounded-xl border bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all font-medium disabled:opacity-40 flex items-center justify-center gap-2"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Registrar
        </button>
      </div>
    </form>
  );
}
